import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Row,
  Col,
  Button,
  Tag,
  Space,
  Form,
  Modal,
  Input,
  message,
  Spin,
  Upload,
  InputNumber,
  Checkbox,
  Pagination,
  Select,
  Progress,
  Tooltip,
  Table,
  Popconfirm,
  Tabs
} from 'antd';
import {
  PlayCircleOutlined,
  VideoCameraOutlined,
  ScissorOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
  DeleteOutlined,
  CopyOutlined,
  EditOutlined,
  CheckSquareOutlined,
  CloseSquareOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  EyeOutlined,
  StopOutlined,
  PlaySquareOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  FileTextOutlined,
  DesktopOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import AssessmentTab from './AssessmentComponents/AssessmentTab';
import WebBroadcastModal from './WebBroadcastModal';
import {
  getSessionsByModule,
  scheduleRTMPSession,
  updateRTMPSession,
  deleteRTMPSession,
  updateRTMPSessionStatus
} from '../../../Service/Schedule/rtmpSession_API';
import {
  getVideos,
  selectVideos,
  selectVideosPagination,
  setCurrentCourse,
  setCurrentModule,
  clearVideoIndexState
} from '../../../Store/VideoIndex/AdminVideoIndexReducer';
import {
  selectVideoUpload,
  copyVideo,
  deleteVideo,
  trimVideo,
  fetchVideoProgress,
  fetchVideoUploadInit
} from '../../../Store/VideoUpload/adminVideoUploadReducer';
import { uploadVideoFile, getVideoDownloadUrl } from '../../../Service/VideoIndex_API/videoIndexApi';
import { fetchPublicIp } from '../../../Service/VideoWatchTrack_API/videoWatchTrackApi';
import { fetchCourses } from '../../../Service/Courses_API/coursesApi';
import { fetchModules } from '../../../Service/Modules_API/modulesApi';
import VideoPlayerPage from '../VideoPlayer/VideoPlayerPage';
import VideoEdit from './VideoEdit';
import { SERVER_URL, getHeaders, handleResponse } from '../../../Service/apiConfig';
import { fetchInstructors } from '../../../Service/Instructor_API/instructorApi';

const { Option } = Select;

const parseDate = (dateStr) => {
  if (!dateStr) return new Date(NaN);
  const s = typeof dateStr === 'string' ? dateStr.replace(' ', 'T') : dateStr;
  return new Date(s);
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = parseDate(dateStr);
  if (isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const format12HourDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = parseDate(dateStr);
  if (isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const formatForDatetimeInput = (dateStr) => {
  if (!dateStr) return '';
  const d = parseDate(dateStr);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const VideoIndexPage = ({ course, module, userRole, onBack }) => {
  const dispatch = useDispatch();

  // Redux selectors
  const videos = useSelector(selectVideos);
  const pagination = useSelector(selectVideosPagination);
  const uploadState = useSelector(selectVideoUpload) || {};
  const progressByVideoId = useMemo(() => uploadState.progressByVideoId || {}, [uploadState.progressByVideoId]);

  // Component local states
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  // View state: 'list' | 'upload'
  const [viewMode, setViewMode] = useState('list');
  const [uploadProgress, setUploadProgress] = useState(0);

  // Play video state
  const [selectedVideo, setSelectedVideo] = useState(() => {
    const saved = localStorage.getItem('selectedVideo');
    try {
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });

  // Modal states
  const [isTrimModalOpen, setIsTrimModalOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);

  // Form states
  const [editingItem, setEditingItem] = useState(null);
  const [uploadForm] = Form.useForm();
  const [trimForm] = Form.useForm();
  const [copyForm] = Form.useForm();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm] = Form.useForm();

  // File list for upload
  const [fileList, setFileList] = useState([]);

  // Copy modal dropdown options list
  const [coursesList, setCoursesList] = useState([]);
  const [modulesList, setModulesList] = useState([]);
  const [copyTargetCourse, setCopyTargetCourse] = useState('');
  const [copyTargetModule, setCopyTargetModule] = useState('');
  const [isBulkCopy, setIsBulkCopy] = useState(false);
  const [instructorsList, setInstructorsList] = useState([]);

  // RTMP Session states
  const [rtmpSessions, setRtmpSessions] = useState([]);
  const [rtmpLoading, setRtmpLoading] = useState(false);
  const [isRtmpModalOpen, setIsRtmpModalOpen] = useState(false);
  const [editingRtmpSession, setEditingRtmpSession] = useState(null);
  const [rtmpForm] = Form.useForm();
  
  // Page Main Tab state: 'videos' | 'rtmp'
  const [activeMainTab, setActiveMainTab] = useState('videos');

  // RTMP Table Filter/Sort & Tab states
  const [activeRtmpTab, setActiveRtmpTab] = useState('scheduled'); // 'scheduled' | 'archived'
  const [rtmpSearch, setRtmpSearch] = useState('');
  const [rtmpStatusFilter, setRtmpStatusFilter] = useState('ALL');
  const [rtmpSortOrder, setRtmpSortOrder] = useState('newest');
  const [rtmpStartDate, setRtmpStartDate] = useState('');
  const [rtmpEndDate, setRtmpEndDate] = useState('');

  // RTMP Details Modal state
  const [isRtmpDetailsModalOpen, setIsRtmpDetailsModalOpen] = useState(false);
  const [selectedRtmpDetails, setSelectedRtmpDetails] = useState(null);
  const [webBroadcastSession, setWebBroadcastSession] = useState(null);
  const [isWebBroadcastOpen, setIsWebBroadcastOpen] = useState(false);
  const startWebAfterSaveRef = useRef(false);

  // Archived RTMP Sessions selection state
  const [selectedRtmpRowKeys, setSelectedRtmpRowKeys] = useState([]);
  const [rtmpBulkDeleting, setRtmpBulkDeleting] = useState(false);

  useEffect(() => {
    const loadInstructors = async () => {
      try {
        const list = await fetchInstructors();
        setInstructorsList(list || []);
      } catch (err) {
        console.warn('Failed to load instructors list:', err);
      }
    };
    loadInstructors();
  }, []);

  const scheduledCount = useMemo(() => {
    return rtmpSessions.filter(s => s.rtmp_status === 'SCHEDULED' || s.rtmp_status === 'LIVE').length;
  }, [rtmpSessions]);

  const archivedCount = useMemo(() => {
    return rtmpSessions.filter(s => s.rtmp_status === 'COMPLETED' || s.rtmp_status === 'CANCELLED').length;
  }, [rtmpSessions]);

  const loadRtmpSessionsList = useCallback(async (isSilent = false) => {
    if (!module?.modules_id) return;
    if (!isSilent) setRtmpLoading(true);
    try {
      const res = await getSessionsByModule(module.modules_id);
      if (res && res.sessions) {
        setRtmpSessions(res.sessions);
      }
    } catch (err) {
      console.warn('Failed to load RTMP sessions:', err.message);
    } finally {
      if (!isSilent) setRtmpLoading(false);
    }
  }, [module?.modules_id]);

  useEffect(() => {
    if (module?.modules_id) {
      loadRtmpSessionsList(false);
      const interval = setInterval(() => {
        loadRtmpSessionsList(true);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [module?.modules_id, loadRtmpSessionsList]);

  const handleSaveRtmpSession = async (values) => {
    try {
      const startTime = values.rtmps_schedul_start_time;
      const endTime = values.rtmps_schedul_end_time;

      if (parseDate(endTime).getTime() <= parseDate(startTime).getTime()) {
        message.error('End time must be after start time.');
        return;
      }

      const payload = {
        courses_id: course?.courses_id,
        modules_id: module?.modules_id,
        instructor_id: values.instructor_id || null,
        video_title: values.video_title,
        vid_cat_id: values.vid_cat_id,
        rtmps_schedul_start_time: startTime,
        rtmps_schedul_end_time: endTime,
        rtmp_url: values.rtmp_url || editingRtmpSession?.rtmp_url || 'rtmp://api.mahamayafoundation.org:1936/live',
        rtmp_stream_key: values.rtmp_stream_key || editingRtmpSession?.rtmp_stream_key || `live_${module?.modules_id || 0}_${Date.now()}`
      };

      let createdOrUpdatedSession = null;
      if (editingRtmpSession) {
        const res = await updateRTMPSession(editingRtmpSession.rtmp_session_id, payload);
        if (res.success) {
          message.success('✅ RTMP schedule updated successfully.');
          createdOrUpdatedSession = {
            ...editingRtmpSession,
            ...payload,
            rtmp_session_id: editingRtmpSession.rtmp_session_id
          };
        } else {
          message.error(res.message || 'Failed to update schedule.');
          startWebAfterSaveRef.current = false;
          return;
        }
      } else {
        const res = await scheduleRTMPSession(payload);
        if (res.success) {
          message.success('✅ RTMP session scheduled successfully.');
          createdOrUpdatedSession = {
            ...payload,
            rtmp_session_id: res.rtmp_session_id || res.session_id,
            session_id: res.session_id || res.rtmp_session_id,
            video_id: res.video_id,
            rtmp_stream_key: res.rtmp_stream_key || payload.rtmp_stream_key,
            rtmp_url: res.rtmp_url || payload.rtmp_url,
            rtmp_status: 'SCHEDULED'
          };
        } else {
          message.error(res.message || 'Failed to schedule session.');
          startWebAfterSaveRef.current = false;
          return;
        }
      }

      const shouldOpenWebStudio = startWebAfterSaveRef.current;
      startWebAfterSaveRef.current = false;

      setIsRtmpModalOpen(false);
      rtmpForm.resetFields();
      setEditingRtmpSession(null);
      loadRtmpSessionsList();
      loadVideosList();

      if (shouldOpenWebStudio && createdOrUpdatedSession) {
        setWebBroadcastSession(createdOrUpdatedSession);
      }
    } catch (err) {
      message.error(err.message || 'Failed to save RTMP schedule.');
    }
  };

  const handleDeleteRtmpSession = async (sessionId) => {
    try {
      const res = await deleteRTMPSession(sessionId);
      if (res.success) {
        message.success('RTMP Session deleted successfully.');
        setSelectedRtmpRowKeys(prev => prev.filter(id => id !== sessionId));
        loadRtmpSessionsList();
        loadVideosList();
      } else {
        message.error(res.message || 'Failed to delete session.');
      }
    } catch (err) {
      message.error(err.message || 'Failed to delete session.');
    }
  };

  const handleBulkDeleteRtmpSessions = async () => {
    if (selectedRtmpRowKeys.length === 0) return;
    setRtmpBulkDeleting(true);
    try {
      let successCount = 0;
      let failCount = 0;
      for (const sessionId of selectedRtmpRowKeys) {
        try {
          const res = await deleteRTMPSession(sessionId);
          if (res && res.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch {
          failCount++;
        }
      }
      if (successCount > 0) {
        message.success(`Deleted ${successCount} archived session(s) successfully.${failCount > 0 ? ` ${failCount} failed.` : ''}`);
      } else {
        message.error('Failed to delete selected sessions.');
      }
      setSelectedRtmpRowKeys([]);
      loadRtmpSessionsList();
      loadVideosList();
    } catch (err) {
      message.error('An error occurred during bulk deletion.');
    } finally {
      setRtmpBulkDeleting(false);
    }
  };

  const handleUpdateStatusRtmpSession = async (sessionId, newStatus) => {
    try {
      const res = await updateRTMPSessionStatus(sessionId, newStatus);
      if (res.success) {
        message.success(`Session status updated to ${newStatus}.`);
        loadRtmpSessionsList();
        loadVideosList();
      } else {
        message.error(res.message || 'Failed to update status.');
      }
    } catch (err) {
      message.error(err.message || 'Failed to update status.');
    }
  };

  // Wrapper with confirmation for destructive status changes
  const handleStatusWithConfirm = (sessionId, newStatus, sessionTitle) => {
    if (newStatus === 'COMPLETED') {
      Modal.confirm({
        title: '⏹️ Stop Live Session?',
        content: (
          <div>
            <p>Are you sure you want to stop <strong>"{sessionTitle}"</strong>?</p>
            <p style={{ color: '#ef4444', marginTop: 8 }}>This will:</p>
            <ul style={{ paddingLeft: 20, color: '#374151' }}>
              <li>Stop the live stream immediately</li>
              <li>Stop recording</li>
              <li>Transcode the recording to MP4</li>
              <li>Generate thumbnail image</li>
              <li>Save video to the module</li>
            </ul>
          </div>
        ),
        okText: 'Yes, Stop & Save',
        okButtonProps: { danger: true },
        cancelText: 'Keep Streaming',
        onOk: () => handleUpdateStatusRtmpSession(sessionId, 'COMPLETED'),
      });
    } else if (newStatus === 'CANCELLED') {
      Modal.confirm({
        title: '🚫 Cancel Session?',
        content: (
          <div>
            <p>Are you sure you want to cancel <strong>"{sessionTitle}"</strong>?</p>
            <p style={{ color: '#6b7280' }}>The session will be cancelled and no recording will be saved.</p>
          </div>
        ),
        okText: 'Yes, Cancel Session',
        okButtonProps: { danger: true },
        cancelText: 'Go Back',
        onOk: () => handleUpdateStatusRtmpSession(sessionId, 'CANCELLED'),
      });
    } else {
      handleUpdateStatusRtmpSession(sessionId, newStatus);
    }
  };

  const filteredRtmpSessions = useMemo(() => {
    return rtmpSessions.filter(s => {
      // Tab filter
      if (activeRtmpTab === 'scheduled') {
        if (s.rtmp_status !== 'SCHEDULED' && s.rtmp_status !== 'LIVE') return false;
      } else if (activeRtmpTab === 'archived') {
        if (s.rtmp_status !== 'COMPLETED' && s.rtmp_status !== 'CANCELLED') return false;
      }

      // Dropdown status filter
      if (rtmpStatusFilter !== 'ALL' && s.rtmp_status !== rtmpStatusFilter) {
        return false;
      }

      // Search query filter
      if (rtmpSearch.trim()) {
        const q = rtmpSearch.toLowerCase().trim();
        const titleMatch = (s.video_title || '').toLowerCase().includes(q);
        const keyMatch = (s.rtmp_stream_key || '').toLowerCase().includes(q);
        if (!titleMatch && !keyMatch) return false;
      }

      // Date range filter
      if (rtmpStartDate) {
        const sTime = parseDate(s.rtmps_schedul_start_time).getTime();
        const fTime = parseDate(`${rtmpStartDate}T00:00:00`).getTime();
        if (sTime < fTime) return false;
      }
      if (rtmpEndDate) {
        const sTime = parseDate(s.rtmps_schedul_start_time).getTime();
        const fTime = parseDate(`${rtmpEndDate}T23:59:59`).getTime();
        if (sTime > fTime) return false;
      }
      return true;
    }).sort((a, b) => {
      const tA = parseDate(a.rtmps_schedul_start_time || a.created_at).getTime();
      const tB = parseDate(b.rtmps_schedul_start_time || b.created_at).getTime();
      if (rtmpSortOrder === 'newest') return tB - tA;
      if (rtmpSortOrder === 'oldest') return tA - tB;
      if (rtmpSortOrder === 'upcoming') {
        const now = Date.now();
        if (tA >= now && tB >= now) return tA - tB;
        if (tA >= now) return -1;
        if (tB >= now) return 1;
        return tB - tA;
      }
      return tB - tA;
    });
  }, [rtmpSessions, activeRtmpTab, rtmpStatusFilter, rtmpSearch, rtmpStartDate, rtmpEndDate, rtmpSortOrder]);

  const getRtmpStatusBadge = (status) => {
    switch (status) {
      case 'SCHEDULED':
        return <Tag color="blue" style={{ fontWeight: 600 }}>SCHEDULED</Tag>;
      case 'LIVE':
        return <Tag color="green" style={{ fontWeight: 600 }}>🟢 LIVE</Tag>;
      case 'COMPLETED':
        return <Tag color="default" style={{ fontWeight: 600 }}>COMPLETED</Tag>;
      case 'CANCELLED':
        return <Tag color="red" style={{ fontWeight: 600 }}>CANCELLED</Tag>;
      default:
        return <Tag color="blue">{status || 'SCHEDULED'}</Tag>;
    }
  };

  // Save selected video to local storage
  useEffect(() => {
    if (selectedVideo) {
      localStorage.setItem('selectedVideo', JSON.stringify(selectedVideo));
    } else {
      localStorage.removeItem('selectedVideo');
    }
  }, [selectedVideo]);

  // Sync current course and module in Redux store
  useEffect(() => {
    if (course) dispatch(setCurrentCourse(course));
    if (module) dispatch(setCurrentModule(module));

    return () => {
      dispatch(clearVideoIndexState());
    };
  }, [course, module, dispatch]);

  // Fetch upload initialization data (next Sl No)
  useEffect(() => {
    if (viewMode === 'upload' && module?.modules_id) {
      dispatch(fetchVideoUploadInit(module.modules_id));
    }
  }, [viewMode, module?.modules_id, dispatch]);

  // Sync Form Sl No. field with next_vid_cat_id
  useEffect(() => {
    if (uploadState.next_vid_cat_id !== undefined) {
      if (viewMode === 'upload') {
        uploadForm.setFieldsValue({ vid_cat_id: uploadState.next_vid_cat_id });
      }
      if (isRtmpModalOpen && !editingRtmpSession) {
        rtmpForm.setFieldsValue({ vid_cat_id: uploadState.next_vid_cat_id });
      }
    }
  }, [uploadState.next_vid_cat_id, viewMode, isRtmpModalOpen, editingRtmpSession, uploadForm, rtmpForm]);

  // Fetch instructors dropdown list
  useEffect(() => {
    if (viewMode === 'upload') {
      fetchInstructors()
        .then((data) => setInstructorsList(data))
        .catch(() => message.error('Failed to load instructors.'));
    }
  }, [viewMode]);

  // Fetch videos list
  const loadVideosList = useCallback(async () => {
    if (!module?.modules_id) return;
    setLoading(true);
    try {
      await dispatch(
        getVideos({
          module_id: module.modules_id,
          search,
          sortOrder,
          page: currentPage,
          limit: pageSize,
          userRole
        })
      ).unwrap();
    } catch (err) {
      message.error(err || 'Failed to load videos.');
    } finally {
      setLoading(false);
    }
  }, [module?.modules_id, search, sortOrder, currentPage, pageSize, userRole, dispatch]);

  const displayedVideos = useMemo(() => {
    return videos;
  }, [videos]);

  useEffect(() => {
    if (viewMode === 'list') {
      loadVideosList();
    }
  }, [loadVideosList, viewMode]);

  // Reset selection on pagination/filter changes
  useEffect(() => {
    setSelectedVideos(new Set());
  }, [videos, currentPage, pageSize]);

  // Initialize progress checks for processing videos
  useEffect(() => {
    videos.forEach((video) => {
      const isUploadedProcessing = video.video_type === 'UPLOADED' && video.video_file_name && video.video_file_name.startsWith('video-');
      const isLiveProcessing = video.video_type === 'LIVE' && (video.video_file_name === 'processing.mp4' || !video.video_file_name);
      
      if (
        (isUploadedProcessing || isLiveProcessing) &&
        progressByVideoId[video.video_id] === undefined
      ) {
        dispatch(
          fetchVideoProgress({
            courseId: course?.courses_id,
            moduleId: module?.modules_id,
            videoId: video.video_id
          })
        );
      }
    });
  }, [videos, dispatch, course?.courses_id, module?.modules_id, progressByVideoId]);

  // Poll progress for processing videos
  useEffect(() => {
    if (videos.length === 0) return;

    const interval = setInterval(() => {
      videos.forEach((video) => {
        const videoId = video.video_id;
        const progress = progressByVideoId[videoId];
        if (progress !== undefined && progress < 100) {
          dispatch(
            fetchVideoProgress({
              courseId: course?.courses_id,
              moduleId: module?.modules_id,
              videoId
            })
          );
        }
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [dispatch, videos, progressByVideoId, course?.courses_id, module?.modules_id]);

  // Helper check if video is transcoding
  const isVideoProcessing = (video) => {
    const progress = progressByVideoId[video.video_id];
    return progress !== undefined && progress < 100;
  };

  const getVideoProgressPercent = (video) => {
    return progressByVideoId[video.video_id] || 0;
  };

  // Selection handlers
  const handleSelectVideo = (videoId) => {
    const newSelected = new Set(selectedVideos);
    if (newSelected.has(videoId)) {
      newSelected.delete(videoId);
    } else {
      newSelected.add(videoId);
    }
    setSelectedVideos(newSelected);
  };

  const handleToggleSelectAll = () => {
    if (selectedVideos.size === videos.length) {
      setSelectedVideos(new Set());
    } else {
      const allIds = videos.map((v) => v.video_id);
      setSelectedVideos(new Set(allIds));
    }
  };

  // Single operations
  const handleDeleteSingle = (videoId) => {
    Modal.confirm({
      title: 'Delete Video',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: 'Are you sure you want to delete this video? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      onOk: async () => {
        try {
          await dispatch(deleteVideo({ videoId })).unwrap();
          message.success('Video deleted successfully.');
          loadVideosList();
        } catch (err) {
          message.error(err || 'Failed to delete video.');
        }
      }
    });
  };

  // Download video handler for ADMIN role
  const handleDownloadVideo = (video) => {
    if (userRole !== 'ADMIN') return;
    if (!video || !video.video_id) return;
    if (!video.video_file_name || video.video_file_name === 'processing.mp4') {
      message.warning('This video is currently processing or has no media file available yet.');
      return;
    }
    const downloadUrl = getVideoDownloadUrl(video.video_id);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.setAttribute('download', '');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    message.success(`Starting download for "${video.video_title || 'video'}"...`);
  };

  // Open copy modal for single video
  const handleOpenCopySingle = (video) => {
    setEditingItem(video);
    setIsBulkCopy(false);
    setIsCopyModalOpen(true);
    loadCoursesDropdown();
  };

  // Open copy modal for bulk copy
  const handleOpenCopyBulk = () => {
    setIsBulkCopy(true);
    setIsCopyModalOpen(true);
    loadCoursesDropdown();
  };

  // Fetch dropdown courses
  const loadCoursesDropdown = async () => {
    try {
      const data = await fetchCourses();
      setCoursesList(data);
    } catch (err) {
      message.error('Failed to load courses.');
    }
  };

  // Fetch dropdown modules when course changes
  useEffect(() => {
    if (copyTargetCourse) {
      fetchModules(copyTargetCourse)
        .then((data) => setModulesList(data))
        .catch(() => message.error('Failed to load modules.'));
    } else {
      setModulesList([]);
    }
    setCopyTargetModule('');
  }, [copyTargetCourse]);

  // Copy submit handler
  const handleCopySubmit = async () => {
    if (!copyTargetCourse || !copyTargetModule) {
      return message.error('Please select target course and module.');
    }

    try {
      if (isBulkCopy) {
        // Bulk Copy
        const orderedSelectedVideos = videos
          .filter((v) => selectedVideos.has(v.video_id))
          .sort((a, b) => (a.video_serial_number || 0) - (b.video_serial_number || 0));

        for (const video of orderedSelectedVideos) {
          const formData = new FormData();
          formData.append('video_id', video.video_id);
          formData.append('courses_id', copyTargetCourse);
          formData.append('module_id', copyTargetModule);
          await dispatch(copyVideo(formData)).unwrap();
        }
        message.success(`Successfully copied ${orderedSelectedVideos.length} videos.`);
      } else {
        // Single Copy
        const formData = new FormData();
        formData.append('video_id', editingItem.video_id);
        formData.append('courses_id', copyTargetCourse);
        formData.append('module_id', copyTargetModule);
        if (editingItem.video_serial_number) {
          formData.append('sort_order', editingItem.video_serial_number);
        }
        await dispatch(copyVideo(formData)).unwrap();
        message.success('Video copied successfully.');
      }

      setIsCopyModalOpen(false);
      setSelectedVideos(new Set());
      setEditingItem(null);
      copyForm.resetFields();
      setCopyTargetCourse('');
      setCopyTargetModule('');
      loadVideosList();
    } catch (err) {
      message.error(err || 'Failed to copy video.');
    }
  };

  // Bulk Delete submits
  const handleBulkDeleteSubmit = async () => {
    const list = Array.from(selectedVideos);
    try {
      let successCount = 0;
      let failCount = 0;

      for (const videoId of list) {
        try {
          await dispatch(deleteVideo({ videoId })).unwrap();
          successCount++;
        } catch {
          failCount++;
        }
      }

      message.success(`Deleted ${successCount} videos successfully.${failCount > 0 ? ` ${failCount} failed.` : ''}`);
      setIsBulkDeleteModalOpen(false);
      setSelectedVideos(new Set());
      loadVideosList();
    } catch (err) {
      message.error('An error occurred during bulk deletion.');
    }
  };

  // Open Edit Metadata modal
  const handleOpenEditModal = (video) => {
    setEditingItem(video);
    editForm.setFieldsValue({
      video_title: video.video_title,
      video_description: video.video_description,
      instructor_id: video.instructor_id,
      video_serial_number: video.video_serial_number
    });
    setIsEditModalOpen(true);
    // Ensure instructorsList is loaded
    if (instructorsList.length === 0) {
      fetchInstructors()
        .then((data) => setInstructorsList(data))
        .catch(() => message.error('Failed to load instructors.'));
    }
  };

  // Submit Edit Metadata
  const handleEditVideoSubmit = async (values) => {
    try {
      const response = await fetch(`${SERVER_URL}/api/video-index/${editingItem.video_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          video_title: values.video_title,
          video_description: values.video_description,
          instructor_id: values.instructor_id,
          video_serial_number: values.video_serial_number
        })
      });
      await handleResponse(response);

      message.success('Video updated successfully.');
      setIsEditModalOpen(false);
      editForm.resetFields();
      setEditingItem(null);
      loadVideosList();
    } catch (err) {
      message.error('Failed to update video: ' + (err.message || err));
    }
  };

  // Upload video handler with progress tracking
  const handleUploadVideo = async (values) => {
    if (fileList.length === 0) {
      return message.error('Please select a video file to upload.');
    }

    const publicIp = await fetchPublicIp();
    const formData = new FormData();
    formData.append('video_file', fileList[0]);
    formData.append('courses_id', course?.courses_id || '');
    formData.append('module_id', module?.modules_id || '');
    formData.append('vid_cat_id', values.vid_cat_id);
    formData.append('vid_title', values.vid_title);
    formData.append('vid_desc', values.vid_desc || '');
    formData.append('video_bit', values.video_bit || '700k');
    formData.append('video_size', values.video_size || '1280x720');
    formData.append('codec', values.codec || 'libx264');
    formData.append('preset', values.preset || 'ultrafast');
    formData.append('instructor_id', values.instructor_id || '');
    if (publicIp) {
      formData.append('public_ip', publicIp);
    }

    try {
      setUploadProgress(1); // Start indicator

      const res = await uploadVideoFile(formData, (event) => {
        const pct = Math.round((event.loaded * 100) / event.total);
        setUploadProgress(pct > 0 ? pct : 1);
      });

      if (res.success) {
        message.success('✅ Video uploaded successfully!');
        setUploadProgress(100);

        setTimeout(() => {
          setViewMode('list');
          setFileList([]);
          uploadForm.resetFields();
          setUploadProgress(0);
          loadVideosList();
        }, 1000);
      } else {
        message.error('❌ ' + (res.message || 'Upload failed'));
        setUploadProgress(0);
      }
    } catch (err) {
      message.error('❌ Upload failed: ' + (err.message || err));
      setUploadProgress(0);
    }
  };

  // Trim video handler
  const handleTrimVideo = async (values) => {
    try {
      await dispatch(
        trimVideo({
          video_id: editingItem.video_id,
          startTime: values.startTime,
          duration: values.duration
        })
      ).unwrap();
      message.success('Video segment trimmed successfully.');
      setIsTrimModalOpen(false);
      trimForm.resetFields();
      setEditingItem(null);
      loadVideosList();
    } catch (err) {
      message.error(err || 'Failed to trim video.');
    }
  };

  if (selectedVideo) {
    if (window.location.pathname !== '/video-player') {
      window.history.pushState({}, '', `/video-player?v=${selectedVideo.video_id}`);
    }
    return (
      <VideoPlayerPage
        video={selectedVideo}
        course={course}
        module={module}
        userRole={userRole}
        onBack={() => {
          setSelectedVideo(null);
          window.history.pushState({}, '', `/module-videos?moduleId=${module?.modules_id || ''}`);
          loadVideosList();
        }}
      />
    );
  }

  if (viewMode === 'trim') {
    if (window.location.pathname !== '/video-edit') {
      window.history.pushState({}, '', `/video-edit?id=${editingItem?.video_id || ''}`);
    }
    return (
      <VideoEdit
        video_id={editingItem?.video_id}
        onBack={() => {
          setViewMode('list');
          setEditingItem(null);
          window.history.pushState({}, '', `/module-videos?moduleId=${module?.modules_id || ''}`);
          loadVideosList();
        }}
      />
    );
  }

  // START FROM WEB — DEDICATED STUDIO & RECORDER PAGE VIEW MODE
  if (isWebBroadcastOpen || webBroadcastSession) {
    return (
      <WebBroadcastModal
        open={true}
        session={webBroadcastSession}
        course={course}
        module={module}
        courseName={course?.courses_name}
        moduleName={module?.modules_name}
        instructorsList={instructorsList}
        defaultSlNo={uploadState.next_vid_cat_id || 1}
        onClose={() => {
          setIsWebBroadcastOpen(false);
          setWebBroadcastSession(null);
        }}
        onSessionUpdated={() => {
          loadRtmpSessionsList();
          loadVideosList();
        }}
      />
    );
  }

  // UPLOAD FORM SUB-PAGE VIEW MODE
  if (viewMode === 'upload') {
    return (
      <div style={{ padding: 24, background: 'var(--panel-color)', minHeight: '80vh', color: 'var(--text-color)', border: '1px solid var(--border-color)', borderRadius: 12 }}>
        {/* Top Header Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 20 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => setViewMode('list')} style={{ borderRadius: 6 }}>
            Back to Videos
          </Button>
        </div>

        <Card
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <span style={{ color: 'var(--text-color)', fontWeight: 'bold' }}>UPLOAD VIDEO</span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Tag color="blue" style={{ borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 500, margin: 0 }}>
                  Course: {course?.courses_name || 'N/A'}
                </Tag>
                <Tag color="purple" style={{ borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 500, margin: 0 }}>
                  Module: {module?.modules_name || 'N/A'}
                </Tag>
              </div>
            </div>
          }
          style={{ border: '1px solid var(--border-color)', borderRadius: 12, color: 'var(--text-color)' }}
          headStyle={{ borderBottom: '1px solid var(--border-color)' }}
        >
          <Form
            form={uploadForm}
            layout="vertical"
            initialValues={{
              vid_cat_id: uploadState.next_vid_cat_id || 1,
              video_bit: '700k',
              video_size: '1280x720',
              codec: 'libx264',
              preset: 'ultrafast'
            }}
            onFinish={handleUploadVideo}
          >
            <Row gutter={16}>
              <Col xs={24} md={4}>
                <Form.Item
                  name="vid_cat_id"
                  label={<span style={{ color: 'var(--text-color)' }}>Sl No.</span>}
                  rules={[{ required: true, message: 'Required!' }]}
                >
                  <InputNumber style={{ width: '100%' }} min={1} />
                </Form.Item>
              </Col>
              <Col xs={24} md={20}>
                <Form.Item
                  name="vid_title"
                  label={<span style={{ color: 'var(--text-color)' }}>Video Title</span>}
                  rules={[{ required: true, message: 'Please enter video title' }]}
                >
                  <Input placeholder="Enter video title" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="vid_desc"
              label={<span style={{ color: 'var(--text-color)' }}>Video Description</span>}
            >
              <Input.TextArea placeholder="Enter video description" rows={3} />
            </Form.Item>

            <Form.Item
              name="instructor_id"
              label={<span style={{ color: 'var(--text-color)' }}>Instructor</span>}
              rules={[{ required: true, message: 'Please select an instructor' }]}
            >
              <Select showSearch optionFilterProp="children"
                placeholder="Select instructor"
               
                style={{ width: '100%' }}
              >
                {instructorsList
                  .filter((inst) => inst.status === 'Active')
                  .map((inst) => (
                    <Option key={inst.instructor_id} value={inst.instructor_id}>
                      {inst.instructor_name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={<span style={{ color: 'var(--text-color)', fontWeight: 'bold' }}>Choose Video File</span>}
              required
            >
              <Upload
                beforeUpload={(file) => {
                  setFileList([file]);
                  return false;
                }}
                fileList={fileList}
                maxCount={1}
                onRemove={() => setFileList([])}
              >
                <Button icon={<UploadOutlined />}>Select Video File</Button>
              </Upload>
            </Form.Item>

            <Card
              title={<span style={{ color: 'var(--text-color)', fontSize: 14 }}>Video Options</span>}
              style={{ background: 'var(--panel-color, #ffffff)', border: '1px dashed var(--border-color)', marginBottom: 24 }}
              bodyStyle={{ padding: 16 }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    name="video_bit"
                    label={<span style={{ color: 'var(--text-secondary)' }}>Video Bitrate</span>}
                  >
                    <Select showSearch optionFilterProp="children">
                      <Option value="400k">400 Kbps</Option>
                      <Option value="500k">500 Kbps</Option>
                      <Option value="600k">600 Kbps</Option>
                      <Option value="700k">700 Kbps</Option>
                      <Option value="800k">800 Kbps</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    name="video_size"
                    label={<span style={{ color: 'var(--text-secondary)' }}>Resolution</span>}
                  >
                    <Select showSearch optionFilterProp="children">
                      <Option value="426x240">426x240</Option>
                      <Option value="640x360">640x360</Option>
                      <Option value="854x480">854x480</Option>
                      <Option value="1024x576">1024x576</Option>
                      <Option value="1280x720">1280x720</Option>
                      <Option value="1920x1080">1920x1080</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    name="codec"
                    label={<span style={{ color: 'var(--text-secondary)' }}>Codec</span>}
                  >
                    <Select showSearch optionFilterProp="children">
                      <Option value="libx264">x264</Option>
                      <Option value="libx265" disabled>x265</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    name="preset"
                    label={<span style={{ color: 'var(--text-secondary)' }}>Processing Speed</span>}
                  >
                    <Select showSearch optionFilterProp="children">
                      <Option value="ultrafast">Ultrafast</Option>
                      <Option value="superfast">Superfast</Option>
                      <Option value="veryfast">Veryfast</Option>
                      <Option value="faster">Faster</Option>
                      <Option value="fast">Fast</Option>
                      <Option value="medium">Medium</Option>
                      <Option value="slow">Slow</Option>
                      <Option value="slower">Slower</Option>
                      <Option value="veryslow">Veryslow</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {uploadProgress > 0 && (
              <div style={{ marginBottom: 20 }}>
                <Progress percent={uploadProgress} status="active" strokeColor="#1890ff" />
                <small style={{ color: '#1890ff', display: 'block', textAlign: 'center', marginTop: 4 }}>
                  Uploading: {uploadProgress}%
                </small>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Button onClick={() => setViewMode('list')} size="large">Cancel</Button>
              <Button type="primary" htmlType="submit" size="large" loading={uploadProgress > 0 && uploadProgress < 100}>
                SUBMIT
              </Button>
            </div>
          </Form>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: 'var(--panel-color)', minHeight: '80vh', color: 'var(--text-color)', border: '1px solid var(--border-color)', borderRadius: 12 }}>
      <style>{`
        .video-card-adaptive {
          background: #ffffff !important;
          border: 1px solid var(--border-color, #e2e8f0) !important;
          border-radius: 16px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .video-card-adaptive:hover {
          transform: translateY(-4px);
          border-color: rgba(59, 130, 246, 0.4) !important;
          box-shadow: 0 12px 24px -6px rgba(0, 0, 0, 0.1) !important;
        }
        .video-card-adaptive.selected {
          border-color: #3b82f6 !important;
          box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.2) !important;
        }
        [data-theme='dark'] .video-card-adaptive {
          background: linear-gradient(145deg, rgba(30, 41, 59, 0.6), rgba(15, 23, 42, 0.8)) !important;
          border: 1px solid rgba(255, 255, 255, 0.08) !important;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        }
        [data-theme='dark'] .video-card-adaptive.selected {
          background: linear-gradient(145deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95)) !important;
          border-color: #3b82f6 !important;
          box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.3);
        }
        [data-theme='dark'] .video-card-adaptive:hover {
          box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.5) !important;
        }
        .ant-pagination {
          color: var(--text-secondary) !important;
        }
        .ant-pagination-item a {
          color: var(--text-secondary) !important;
        }
        .ant-pagination-item:hover a,
        .ant-pagination-item-active a {
          color: #818cf8 !important;
        }
        .ant-pagination-item-active {
          background: rgba(99, 102, 241, 0.2) !important;
          border-color: #6366f1 !important;
        }
        .ant-pagination-prev .ant-pagination-item-link,
        .ant-pagination-next .ant-pagination-item-link {
          color: var(--text-secondary) !important;
          background-color: transparent !important;
          border-color: var(--border-color) !important;
        }
        .ant-pagination-prev:hover .ant-pagination-item-link,
        .ant-pagination-next:hover .ant-pagination-item-link {
          color: #818cf8 !important;
          border-color: #6366f1 !important;
        }
        .ant-select-dropdown {
        }
        .ant-select-item {
        }
        .ant-select-item-option-active {
          background-color: var(--border-color) !important;
        }
        .ant-select-item-option-selected {
          background-color: rgba(99, 102, 241, 0.25) !important;
          color: #818cf8 !important;
        }
        .ant-tabs-tab {
          color: var(--text-secondary) !important;
        }
        .ant-tabs-tab:hover {
          color: #818cf8 !important;
        }
        .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #6366f1 !important;
          font-weight: 600 !important;
        }
        .ant-tabs-ink-bar {
          background-color: #6366f1 !important;
        }
        .ant-tabs-nav::before {
          border-bottom-color: var(--border-color) !important;
        }
      `}</style>
      {/* Top Header Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 20 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} style={{ borderRadius: 6 }}>
          Back to Modules
        </Button>
      </div>

      {/* Title & Upload Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={{ color: 'var(--text-color)', margin: 0 }}>
            Module Videos
          </h2>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Tag color="blue" style={{ borderRadius: 6, padding: '2px 10px', fontSize: 13, fontWeight: 500, margin: 0 }}>
              Course: {course?.courses_name || 'N/A'}
            </Tag>
            <Tag color="purple" style={{ borderRadius: 6, padding: '2px 10px', fontSize: 13, fontWeight: 500, margin: 0 }}>
              Module: {module?.modules_name || 'N/A'}
            </Tag>
          </div>
        </div>
        {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
          <Space size="middle" wrap>
            <Button
              type="primary"
              icon={<DesktopOutlined />}
              onClick={() => {
                if (module?.modules_id) {
                  dispatch(fetchVideoUploadInit(module.modules_id));
                }
                setWebBroadcastSession(null);
                setIsWebBroadcastOpen(true);
              }}
              style={{ borderRadius: 6, background: '#8b5cf6', borderColor: '#8b5cf6' }}
            >
              Start From Web
            </Button>
            <Button
              type="primary"
              icon={<VideoCameraOutlined />}
              onClick={() => {
                setEditingRtmpSession(null);
                rtmpForm.resetFields();
                if (module?.modules_id) {
                  dispatch(fetchVideoUploadInit(module.modules_id));
                }
                rtmpForm.setFieldsValue({
                  courses_name: course?.courses_name || 'N/A',
                  modules_name: module?.modules_name || 'N/A',
                  vid_cat_id: uploadState.next_vid_cat_id || 1,
                  rtmp_url: 'rtmp://api.mahamayafoundation.org:1936/live',
                  rtmp_stream_key: `${course?.courses_id || 0}_${module?.modules_id || 0}_${Math.floor(100000 + Math.random() * 900000)}`
                });
                setIsRtmpModalOpen(true);
              }}
              style={{ borderRadius: 6, background: '#10b981', borderColor: '#10b981' }}
            >
              RTMP Schedule
            </Button>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => {
                setViewMode('upload');
                setFileList([]);
                uploadForm.resetFields();
              }}
              style={{ borderRadius: 6 }}
            >
              Upload Video
            </Button>
          </Space>
        )}
      </div>

      {/* Main Page Tabs: Tab 1 = Module Videos, Tab 2 = RTMP Schedules */}
      <Tabs
        activeKey={activeMainTab}
        onChange={setActiveMainTab}
        style={{ marginTop: 8 }}
        items={[
          {
            key: 'videos',
            label: (
              <span style={{ fontSize: 15, fontWeight: 600 }}>
                <PlayCircleOutlined style={{ marginRight: 6 }} /> Module Videos ({pagination.totalItems || videos.length})
              </span>
            ),
            children: (
              <>
                {/* Control Filters Bar */}
                <Row gutter={[16, 16]} align="middle" style={{ background: 'transparent', border: '1px solid var(--border-color)', padding: '16px 20px', borderRadius: 12, marginBottom: 24, marginTop: 16 }}>
                  <Col xs={24} md={8}>
                    <Input
                      placeholder="Search videos by title..."
                      prefix={<SearchOutlined style={{ color: 'var(--text-secondary)' }} />}
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setCurrentPage(1);
                      }}
                      allowClear
                      style={{
                        background: 'var(--panel-color, #ffffff)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-color)',
                        borderRadius: 8
                      }}
                    />
                  </Col>

                  <Col xs={24} md={12} style={{ display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                    {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                      <>
                        <Button
                          icon={selectedVideos.size === videos.length && videos.length > 0 ? <CloseSquareOutlined /> : <CheckSquareOutlined />}
                          disabled={videos.length === 0}
                          onClick={handleToggleSelectAll}
                          style={{ borderRadius: 6 }}
                        >
                          {selectedVideos.size === videos.length && videos.length > 0 ? 'Deselect All' : 'Select All'}
                        </Button>
                        <Button
                          type="primary"
                          icon={<CopyOutlined />}
                          disabled={selectedVideos.size === 0}
                          onClick={handleOpenCopyBulk}
                          style={{ borderRadius: 6, background: '#10b981', borderColor: '#10b981' }}
                        >
                          Copy Selected
                        </Button>
                        <Button
                          danger
                          type="primary"
                          icon={<DeleteOutlined />}
                          disabled={selectedVideos.size === 0}
                          onClick={() => setIsBulkDeleteModalOpen(true)}
                          style={{ borderRadius: 6 }}
                        >
                          Delete Selected
                        </Button>
                      </>
                    )}
                  </Col>

                  <Col xs={24} md={4} style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <Select showSearch optionFilterProp="children"
                      value={sortOrder}
                      onChange={(val) => {
                        setSortOrder(val);
                        setCurrentPage(1);
                      }}
                      style={{ width: '100%' }}
                     
                    >
                      <Option value="DESC">DESC (Newest)</Option>
                      <Option value="ASC">ASC (Oldest)</Option>
                    </Select>
                  </Col>
                </Row>

                {/* Videos Grid */}
                {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                    <Spin size="large" tip="Loading videos..." />
                  </div>
                ) : displayedVideos.length === 0 ? (
                  <Card style={{ border: '1px solid var(--border-color)', borderRadius: 12, textAlign: 'center', padding: '60px 0' }}>
                    <VideoCameraOutlined style={{ fontSize: 48, color: 'var(--border-color)', marginBottom: 16 }} />
                    <p style={{ color: 'var(--text-secondary)', fontSize: 16 }}>No videos found in this module.</p>
                  </Card>
                ) : (
                  <Row gutter={[20, 20]}>
                    {displayedVideos.map((video) => {
                      const isSelected = selectedVideos.has(video.video_id);
                      const processing = isVideoProcessing(video);
                      const progressPercent = getVideoProgressPercent(video);

                      const getThumbnailUrl = () => {
                        const imgFile = video.video_image_file_name;
                        if (!imgFile || imgFile === '16X9.png') return null;
                        if (imgFile.startsWith('http://') || imgFile.startsWith('https://')) {
                          return imgFile;
                        }
                        if (video.vdeo_direactory && !imgFile.includes('/')) {
                          return `${SERVER_URL}/${video.vdeo_direactory}/${imgFile}`;
                        }
                        if (imgFile.startsWith('../') || imgFile.startsWith('modules/')) {
                          return `${SERVER_URL}/Uploads/${imgFile}`;
                        }
                        return `${SERVER_URL}/${video.vdeo_direactory || 'Uploads'}/${imgFile}`;
                      };

                      const thumbUrl = getThumbnailUrl();

                      return (
                        <Col xs={24} sm={12} md={8} lg={8} xl={8} key={video.video_id}>
                          <Card
                            hoverable
                            className={`video-card-adaptive ${isSelected ? 'selected' : ''}`}
                            bodyStyle={{ padding: 18 }}
                            cover={
                              <div
                                style={{
                                  position: 'relative',
                                  width: '100%',
                                  aspectRatio: '16 / 9',
                                  background: '#0f172a',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  overflow: 'hidden',
                                  borderTopLeftRadius: 16,
                                  borderTopRightRadius: 16
                                }}
                              >
                                {thumbUrl ? (
                                  <img
                                    src={thumbUrl}
                                    alt={video.video_title}
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' }} />
                                )}
                                <div
                                  onClick={() => setSelectedVideo(video)}
                                  style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: 52,
                                    height: 52,
                                    borderRadius: '50%',
                                    background: 'rgba(255, 255, 255, 0.25)',
                                    backdropFilter: 'blur(8px)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    border: '1px solid rgba(255, 255, 255, 0.4)',
                                    boxShadow: '0 0 20px rgba(0, 0, 0, 0.5)',
                                    zIndex: 2
                                  }}
                                >
                                  <PlayCircleOutlined style={{ fontSize: 28, color: '#fff' }} />
                                </div>

                                {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                                  <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 3 }}>
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => handleSelectVideo(video.video_id)}
                                      style={{ transform: 'scale(1.2)' }}
                                    />
                                  </div>
                                )}

                                {/* Video Type Badge - Hidden for USER role */}
                                {userRole !== 'USER' && (
                                  <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
                                    <Tag color="blue" style={{ margin: 0, borderRadius: 6, fontWeight: 600 }}>
                                      {video.video_type || 'UPLOADED'}
                                    </Tag>
                                  </div>
                                )}



                                {/* Duration Badge Overlay */}
                                <div
                                  style={{
                                    position: 'absolute',
                                    bottom: 10,
                                    right: 12,
                                    zIndex: 2,
                                    background: 'rgba(0, 0, 0, 0.75)',
                                    backdropFilter: 'blur(4px)',
                                    padding: '3px 8px',
                                    borderRadius: 6,
                                    fontSize: 11,
                                    color: '#fff',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4
                                  }}
                                >
                                  <FieldTimeOutlined />
                                  <span>{video.video_duration || '00:00:00'}</span>
                                </div>
                              </div>
                            }
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {/* Full Wrapping Title */}
                              <h4
                                style={{
                                  color: 'var(--text-color, #1e293b)',
                                  fontSize: 16,
                                  margin: 0,
                                  fontWeight: 600,
                                  wordBreak: 'break-word',
                                  whiteSpace: 'normal',
                                  lineHeight: 1.4,
                                  letterSpacing: '-0.01em'
                                }}
                              >
                                {video.video_serial_number ? `${video.video_serial_number}. ` : ''}{video.video_title}
                              </h4>

                              {/* Description */}
                              {video.video_description && (
                                <p
                                  style={{
                                    color: 'var(--text-secondary, #64748b)',
                                    fontSize: 13,
                                    margin: 0,
                                    lineHeight: 1.5,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                  }}
                                >
                                  {video.video_description}
                                </p>
                              )}

                              {/* Metadata Chips Row */}
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                                {video.instructor_name && (
                                  <Tag
                                    icon={<UserOutlined />}
                                    style={{
                                      margin: 0,
                                      fontSize: 11,
                                      borderRadius: 6,
                                      background: 'rgba(147, 51, 234, 0.12)',
                                      border: '1px solid rgba(147, 51, 234, 0.3)',
                                      color: '#9333ea',
                                      padding: '2px 8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 4
                                    }}
                                  >
                                    {video.instructor_name}
                                  </Tag>
                                )}
                                {video.video_date && (
                                  <Tag
                                    icon={<CalendarOutlined />}
                                    style={{
                                      margin: 0,
                                      fontSize: 11,
                                      borderRadius: 6,
                                      background: 'rgba(6, 182, 212, 0.12)',
                                      border: '1px solid rgba(6, 182, 212, 0.3)',
                                      color: '#0891b2',
                                      padding: '2px 8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 4
                                    }}
                                  >
                                    {formatDate(video.video_date)}
                                  </Tag>
                                )}
                              </div>

                              {/* Transcoding Progress */}
                              {processing && (
                                <div style={{ background: 'rgba(250,173,20,0.06)', border: '1px dashed rgba(250,173,20,0.3)', padding: 10, borderRadius: 8, marginTop: 4 }}>
                                  <Progress percent={progressPercent} status="active" strokeColor="#faad14" size="small" />
                                  <small style={{ color: '#faad14', display: 'block', textAlign: 'center', marginTop: 4 }}>Transcoding video in background...</small>
                                </div>
                              )}

                              {/* Footer Actions */}
                              {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    borderTop: '1px solid var(--border-color, rgba(0, 0, 0, 0.06))',
                                    paddingTop: 10,
                                    marginTop: 6
                                  }}
                                >
                                  <Space size={6}>
                                    {userRole === 'ADMIN' && (
                                      <Tooltip title="Download Video">
                                        <Popconfirm
                                          title="Download Video"
                                          description={`Are you sure you want to download "${video.video_title}"?`}
                                          onConfirm={() => handleDownloadVideo(video)}
                                          okText="Download"
                                          cancelText="Cancel"
                                        >
                                          <Button
                                            size="small"
                                            icon={<DownloadOutlined />}
                                            style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: 6 }}
                                          />
                                        </Popconfirm>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Trim Segment">
                                      <Button
                                        size="small"
                                        icon={<ScissorOutlined />}
                                        onClick={() => {
                                          setEditingItem(video);
                                          setViewMode('trim');
                                        }}
                                        style={{ background: 'var(--panel-color, #ffffff)', color: 'var(--text-color)', border: '1px solid var(--border-color)', borderRadius: 6 }}
                                      />
                                    </Tooltip>
                                    <Tooltip title="Edit Video">
                                      <Button
                                        size="small"
                                        icon={<EditOutlined />}
                                        onClick={() => handleOpenEditModal(video)}
                                        style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#818cf8', border: '1px solid rgba(99, 102, 241, 0.3)', borderRadius: 6 }}
                                      />
                                    </Tooltip>
                                    <Tooltip title="Copy Video">
                                      <Button
                                        size="small"
                                        icon={<CopyOutlined />}
                                        onClick={() => handleOpenCopySingle(video)}
                                        style={{ background: 'rgba(250, 173, 20, 0.1)', color: '#fbbf24', border: '1px solid rgba(250, 173, 20, 0.3)', borderRadius: 6 }}
                                      />
                                    </Tooltip>
                                    <Tooltip title="Delete Video">
                                      <Button
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleDeleteSingle(video.video_id)}
                                        style={{ borderRadius: 6 }}
                                      />
                                    </Tooltip>
                                  </Space>
                                </div>
                              )}
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}

                {/* Pagination component */}
                {pagination.totalItems > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={pagination.totalItems}
                      onChange={(page, size) => {
                        setCurrentPage(page);
                        if (size) setPageSize(size);
                      }}
                      showSizeChanger
                      pageSizeOptions={['5', '10', '20', '50']}
                    />
                  </div>
                )}
              </>
            )
          },
          (userRole === 'ADMIN' || userRole === 'STUDIO') && {
            key: 'rtmp',
            label: (
              <span style={{ fontSize: 15, fontWeight: 600 }}>
                <VideoCameraOutlined style={{ marginRight: 6 }} /> RTMP Schedules ({rtmpSessions.length})
              </span>
            ),
            children: (
              <Card
                style={{ marginTop: 16, border: '1px solid var(--border-color)', borderRadius: 12, color: 'var(--text-color)', background: 'transparent' }}
                bodyStyle={{ padding: 20 }}
              >
                {/* Ant Design Sub-Tabs matching Session Controls */}
                <Tabs
                  activeKey={activeRtmpTab}
                  onChange={(key) => {
                    setActiveRtmpTab(key);
                    setRtmpStatusFilter('ALL');
                    setSelectedRtmpRowKeys([]);
                  }}
                  style={{ marginBottom: 16 }}
                >
                  <Tabs.TabPane
                    tab={
                      <span>
                        <CalendarOutlined style={{ marginRight: 6 }} /> Scheduled Sessions
                      </span>
                    }
                    key="scheduled"
                  />
                  <Tabs.TabPane
                    tab={
                      <span>
                        <ClockCircleOutlined style={{ marginRight: 6 }} /> Archived Sessions
                      </span>
                    }
                    key="archived"
                  />
                </Tabs>

                {/* Search, Filters, and Sorting Controls in ONE single row */}
                <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'nowrap', gap: 10, alignItems: 'center', justifyContent: 'space-between', overflowX: 'auto', paddingBottom: 4 }}>
                  <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 10, alignItems: 'center', flex: 1 }}>
                    <Input
                      placeholder="Search by Title..."
                      prefix={<SearchOutlined style={{ color: 'rgba(255,255,255,0.25)' }} />}
                      value={rtmpSearch}
                      onChange={e => setRtmpSearch(e.target.value)}
                      allowClear
                      style={{ width: 200, borderRadius: 6, flexShrink: 0 }}
                    />
                    <Select showSearch optionFilterProp="children"
                      value={rtmpStatusFilter}
                      onChange={val => setRtmpStatusFilter(val)}
                      style={{ width: 140, flexShrink: 0 }}
                    >
                      {activeRtmpTab === 'scheduled' ? (
                        <>
                          <Option value="ALL">All Scheduled</Option>
                          <Option value="SCHEDULED">Scheduled</Option>
                          <Option value="LIVE">Live</Option>
                        </>
                      ) : (
                        <>
                          <Option value="ALL">All Archived</Option>
                          <Option value="COMPLETED">Completed</Option>
                          <Option value="CANCELLED">Cancelled</Option>
                        </>
                      )}
                    </Select>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>From:</span>
                      <Input
                        type="date"
                        value={rtmpStartDate}
                        onChange={e => setRtmpStartDate(e.target.value)}
                        style={{ width: 130 }}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>To:</span>
                      <Input
                        type="date"
                        value={rtmpEndDate}
                        onChange={e => setRtmpEndDate(e.target.value)}
                        style={{ width: 130 }}
                      />
                    </div>
                    {(rtmpSearch || rtmpStatusFilter !== 'ALL' || rtmpStartDate || rtmpEndDate) && (
                      <Button type="link" onClick={() => {
                        setRtmpSearch('');
                        setRtmpStatusFilter('ALL');
                        setRtmpStartDate('');
                        setRtmpEndDate('');
                      }} style={{ padding: 0, flexShrink: 0 }}>
                        Reset Filters
                      </Button>
                    )}
                    {activeRtmpTab === 'archived' && (userRole === 'ADMIN' || userRole === 'STUDIO') && (
                      <>
                        <Button
                          icon={selectedRtmpRowKeys.length === filteredRtmpSessions.length && filteredRtmpSessions.length > 0 ? <CloseSquareOutlined /> : <CheckSquareOutlined />}
                          disabled={filteredRtmpSessions.length === 0}
                          onClick={() => {
                            if (selectedRtmpRowKeys.length === filteredRtmpSessions.length && filteredRtmpSessions.length > 0) {
                              setSelectedRtmpRowKeys([]);
                            } else {
                              setSelectedRtmpRowKeys(filteredRtmpSessions.map(s => s.rtmp_session_id));
                            }
                          }}
                          style={{ borderRadius: 6, flexShrink: 0 }}
                        >
                          {selectedRtmpRowKeys.length === filteredRtmpSessions.length && filteredRtmpSessions.length > 0 ? 'Deselect All' : 'Select All'}
                        </Button>
                        <Popconfirm
                          title="Delete Selected Archived Sessions?"
                          description={`Are you sure you want to permanently delete ${selectedRtmpRowKeys.length} archived session(s)?`}
                          onConfirm={handleBulkDeleteRtmpSessions}
                          okText="Yes, Delete"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true, loading: rtmpBulkDeleting }}
                          disabled={selectedRtmpRowKeys.length === 0}
                        >
                          <Button
                            danger
                            type="primary"
                            icon={<DeleteOutlined />}
                            disabled={selectedRtmpRowKeys.length === 0}
                            loading={rtmpBulkDeleting}
                            style={{ borderRadius: 6, flexShrink: 0 }}
                          >
                            Delete Selected{selectedRtmpRowKeys.length > 0 ? ` (${selectedRtmpRowKeys.length})` : ''}
                          </Button>
                        </Popconfirm>
                      </>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Sort:</span>
                    <Select showSearch optionFilterProp="children" value={rtmpSortOrder} onChange={val => setRtmpSortOrder(val)} style={{ width: 110 }}>
                      <Option value="newest">Newest</Option>
                      <Option value="oldest">Oldest</Option>
                      <Option value="upcoming">Upcoming</Option>
                    </Select>
                  </div>
                </div>

                {/* Sessions Table */}
                <Table
                  dataSource={filteredRtmpSessions}
                  loading={rtmpLoading}
                  rowKey="rtmp_session_id"
                  rowSelection={
                    activeRtmpTab === 'archived' && (userRole === 'ADMIN' || userRole === 'STUDIO')
                      ? {
                          selectedRowKeys: selectedRtmpRowKeys,
                          onChange: (keys) => setSelectedRtmpRowKeys(keys)
                        }
                      : undefined
                  }
                  size="middle"
                  scroll={{ x: 'max-content' }}
                  pagination={{ pageSize: 10, showSizeChanger: false, showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} sessions` }}
                  columns={[
                    {
                      title: 'Session Title',
                      dataIndex: 'video_title',
                      key: 'video_title',
                      width: '38%',
                      render: (title, rec) => (
                        <div>
                          <strong style={{ color: 'var(--text-color)', fontSize: 14 }}>{title}</strong>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            {rec.rtmp_session_id && <span>Session ID: #{rec.rtmp_session_id}</span>}
                            {(rec.instructor_name || rec.instructor_id) && (
                              <span>
                                Instructor: {rec.instructor_name || (instructorsList.find(i => Number(i.instructor_id) === Number(rec.instructor_id))?.instructor_name) || 'N/A'}
                              </span>
                            )}
                          </div>
                        </div>
                      )
                    },
                    {
                      title: 'Start Time',
                      dataIndex: 'rtmps_schedul_start_time',
                      key: 'rtmps_schedul_start_time',
                      width: '16%',
                      render: dateStr => <span style={{ fontSize: 13, fontWeight: 500 }}>{format12HourDateTime(dateStr)}</span>
                    },
                    {
                      title: 'End Time',
                      dataIndex: 'rtmps_schedul_end_time',
                      key: 'rtmps_schedul_end_time',
                      width: '16%',
                      render: dateStr => <span style={{ fontSize: 13, fontWeight: 500 }}>{format12HourDateTime(dateStr)}</span>
                    },
                    {
                      title: 'Status',
                      dataIndex: 'rtmp_status',
                      key: 'rtmp_status',
                      width: '12%',
                      align: 'center',
                      render: status => getRtmpStatusBadge(status)
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      width: '18%',
                      align: 'center',
                      render: (_, record) => (
                        <Space size={4} wrap>
                          <Tooltip title="View Details">
                            <Button
                              size="small"
                              icon={<EyeOutlined />}
                              onClick={() => {
                                setSelectedRtmpDetails(record);
                                setIsRtmpDetailsModalOpen(true);
                              }}
                            />
                          </Tooltip>
                          {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                            <>
                              {record.rtmp_status === 'SCHEDULED' && (
                                <Tooltip title={record.is_rtmp_live ? "Start Live" : "OBS stream not started yet. Start streaming in OBS first."}>
                                  <span>
                                    <Button
                                      size="small"
                                      type="primary"
                                      icon={<PlaySquareOutlined />}
                                      disabled={!record.is_rtmp_live}
                                      style={record.is_rtmp_live ? { background: '#10b981', borderColor: '#10b981' } : {}}
                                      onClick={() => handleUpdateStatusRtmpSession(record.rtmp_session_id, 'LIVE')}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                              {record.rtmp_status === 'LIVE' && (
                                <Tooltip title="End Live & Save Video">
                                  <Button
                                    size="small"
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    style={{ background: '#3b82f6', borderColor: '#3b82f6' }}
                                    onClick={() => handleStatusWithConfirm(record.rtmp_session_id, 'COMPLETED', record.video_title)}
                                  />
                                </Tooltip>
                              )}
                              {(record.rtmp_status === 'SCHEDULED' || record.rtmp_status === 'LIVE') && (
                                <Tooltip title="Cancel Session">
                                  <Button
                                    size="small"
                                    icon={<StopOutlined />}
                                    style={{ background: '#ef4444', borderColor: '#ef4444', color: '#fff' }}
                                    onClick={() => handleStatusWithConfirm(record.rtmp_session_id, 'CANCELLED', record.video_title)}
                                  />
                                </Tooltip>
                              )}
                              <Tooltip title="Edit Session">
                                <Button
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => {
                                    if (!instructorsList || instructorsList.length === 0) {
                                      fetchInstructors().then(list => setInstructorsList(list || [])).catch(() => {});
                                    }
                                    setEditingRtmpSession(record);
                                    rtmpForm.setFieldsValue({
                                      courses_name: course?.courses_name || 'N/A',
                                      modules_name: module?.modules_name || 'N/A',
                                      vid_cat_id: record.video_serial_number || '',
                                      video_title: record.video_title,
                                      instructor_id: record.instructor_id ? Number(record.instructor_id) : (record.instructor_id || null),
                                      rtmps_schedul_start_time: formatForDatetimeInput(record.rtmps_schedul_start_time),
                                      rtmps_schedul_end_time: formatForDatetimeInput(record.rtmps_schedul_end_time),
                                      rtmp_url: record.rtmp_url,
                                      rtmp_stream_key: record.rtmp_stream_key
                                    });
                                    setIsRtmpModalOpen(true);
                                  }}
                                />
                              </Tooltip>
                              <Popconfirm
                                title="Delete RTMP Session?"
                                description="Are you sure you want to permanently delete this scheduled session?"
                                onConfirm={() => handleDeleteRtmpSession(record.rtmp_session_id)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <Tooltip title="Delete Session">
                                  <Button size="small" danger icon={<DeleteOutlined />} />
                                </Tooltip>
                              </Popconfirm>
                            </>
                          )}
                        </Space>
                      )
                    }
                  ]}
                />
              </Card>
            )
          },
          {
            key: 'assessments',
            label: (
              <span style={{ fontSize: 15, fontWeight: 600 }}>
                <FileTextOutlined style={{ marginRight: 6 }} /> Assessments & Quizzes
              </span>
            ),
            children: (
              <AssessmentTab course={course} module={module} userRole={userRole} />
            )
          }
        ].filter(Boolean)}
      />

      {/* RTMP Schedule Modal */}
      <Modal
        title={editingRtmpSession ? 'Edit RTMP Schedule' : 'Schedule RTMP Session'}
        open={isRtmpModalOpen}
        onCancel={() => {
          setIsRtmpModalOpen(false);
          setEditingRtmpSession(null);
          rtmpForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        centered
        width={600}
      >
        <Form
          form={rtmpForm}
          layout="vertical"
          onFinish={handleSaveRtmpSession}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="courses_name" label="Course">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="modules_name" label="Module">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="vid_cat_id"
                label="Sl No."
                rules={[{ required: true, message: 'Required!' }]}
              >
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col span={18}>
              <Form.Item
                name="video_title"
                label="Session Title"
                rules={[{ required: true, message: 'Please enter session title' }]}
              >
                <Input placeholder="Enter session title" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="instructor_id" label="Instructor">
            <Select placeholder="Select Instructor" allowClear showSearch optionFilterProp="children">
              {instructorsList
                .filter(inst => !inst.status || inst.status.toLowerCase() === 'active' || Number(inst.instructor_id) === Number(editingRtmpSession?.instructor_id))
                .map(inst => (
                  <Select.Option key={inst.instructor_id} value={Number(inst.instructor_id)}>
                    {inst.instructor_name} {inst.instructor_qualification ? `(${inst.instructor_qualification})` : ''}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="rtmps_schedul_start_time"
                label="Schedule Start Date & Time"
                rules={[{ required: true, message: 'Start date and time is required' }]}
              >
                <Input type="datetime-local" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="rtmps_schedul_end_time"
                label="Schedule End Date & Time"
                rules={[{ required: true, message: 'End date and time is required' }]}
              >
                <Input type="datetime-local" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
            <Button onClick={() => {
              setIsRtmpModalOpen(false);
              setEditingRtmpSession(null);
              rtmpForm.resetFields();
            }}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              style={{ background: '#10b981', borderColor: '#10b981' }}
            >
              Save Schedule
            </Button>
          </div>
        </Form>
      </Modal>

      {/* RTMP Session Details Modal */}
      <Modal
        title="RTMP Session Details"
        open={isRtmpDetailsModalOpen}
        onCancel={() => {
          setIsRtmpDetailsModalOpen(false);
          setSelectedRtmpDetails(null);
        }}
        footer={[
          <Button key="close" onClick={() => setIsRtmpDetailsModalOpen(false)}>
            Close
          </Button>
        ]}
        centered
        width={550}
      >
        {selectedRtmpDetails && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 16, fontWeight: 'bold' }}>{selectedRtmpDetails.video_title}</span>
              {getRtmpStatusBadge(selectedRtmpDetails.rtmp_status)}
            </div>

            <Row gutter={[12, 12]}>
              <Col span={12}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Course:</div>
                <div style={{ fontWeight: 500 }}>{course?.courses_name || 'N/A'}</div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Module:</div>
                <div style={{ fontWeight: 500 }}>{module?.modules_name || 'N/A'}</div>
              </Col>
            </Row>

            <Row gutter={[12, 12]}>
              <Col span={12}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Instructor:</div>
                <div style={{ fontWeight: 500 }}>
                  {selectedRtmpDetails.instructor_name
                    ? `${selectedRtmpDetails.instructor_name} ${selectedRtmpDetails.instructor_qualification ? `(${selectedRtmpDetails.instructor_qualification})` : ''}`
                    : (instructorsList.find(i => Number(i.instructor_id) === Number(selectedRtmpDetails.instructor_id))?.instructor_name || 'N/A')}
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Status:</div>
                <div>{getRtmpStatusBadge(selectedRtmpDetails.rtmp_status)}</div>
              </Col>
            </Row>

            <Row gutter={[12, 12]}>
              <Col span={12}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Start Time:</div>
                <div style={{ fontWeight: 500 }}>{format12HourDateTime(selectedRtmpDetails.rtmps_schedul_start_time)}</div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>End Time:</div>
                <div style={{ fontWeight: 500 }}>{format12HourDateTime(selectedRtmpDetails.rtmps_schedul_end_time)}</div>
              </Col>
            </Row>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>RTMP Server URL:</span>
                <Button
                  size="small"
                  type="link"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(selectedRtmpDetails.rtmp_url || '');
                    message.success('RTMP URL copied to clipboard!');
                  }}
                >
                  Copy
                </Button>
              </div>
              <div style={{ background: 'rgba(15, 23, 42, 0.6)', padding: '8px 12px', borderRadius: 6, fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all', border: '1px solid var(--border-color)' }}>
                {selectedRtmpDetails.rtmp_url}
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Stream Key:</span>
                <Button
                  size="small"
                  type="link"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(selectedRtmpDetails.rtmp_stream_key || '');
                    message.success('Stream Key copied to clipboard!');
                  }}
                >
                  Copy
                </Button>
              </div>
              <div style={{ background: 'rgba(15, 23, 42, 0.6)', padding: '8px 12px', borderRadius: 6, fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all', border: '1px solid var(--border-color)', color: '#d8b4fe' }}>
                {selectedRtmpDetails.rtmp_stream_key}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Trim Video Modal */}
      <Modal
        title="Trim Video Segment"
        open={isTrimModalOpen}
        onCancel={() => {
          setIsTrimModalOpen(false);
          setEditingItem(null);
          trimForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        centered
      >
        <Form form={trimForm} layout="vertical" onFinish={handleTrimVideo}>
          <Form.Item name="startTime" label="Start Offset (Seconds)" rules={[{ required: true, message: 'Start offset is required' }]} initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="duration" label="Duration to keep (Seconds)" rules={[{ required: true, message: 'Duration is required' }]} initialValue={10}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
            <Button onClick={() => setIsTrimModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={uploadState.trimming}>
              Trim segment
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Copy Video Modal (Single/Bulk) */}
      <Modal
        title={isBulkCopy ? 'Copy Selected Videos' : 'Copy Video'}
        open={isCopyModalOpen}
        onCancel={() => {
          setIsCopyModalOpen(false);
          setEditingItem(null);
          copyForm.resetFields();
          setCopyTargetCourse('');
          setCopyTargetModule('');
        }}
        footer={null}
        destroyOnClose
        centered
      >
        <div style={{ marginBottom: 16 }}>
          {isBulkCopy ? (
            <Tag color="blue">{selectedVideos.size} videos selected to copy.</Tag>
          ) : (
            <div>
              Copying: <strong style={{ color: '#1890ff' }}>{editingItem?.video_title}</strong>
            </div>
          )}
        </div>
        <Form form={copyForm} layout="vertical" onFinish={handleCopySubmit}>
          <Form.Item label="Target Course" required>
            <Select
              placeholder="Select Course"
              value={copyTargetCourse}
              onChange={(val) => setCopyTargetCourse(val)}
              showSearch
              optionFilterProp="children"
            >
              {coursesList.map((c) => (
                <Option key={c.courses_id} value={c.courses_id}>
                  {c.courses_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Target Module" required>
            <Select
              placeholder="Select Module"
              value={copyTargetModule}
              onChange={(val) => setCopyTargetModule(val)}
              disabled={!copyTargetCourse}
              showSearch
              optionFilterProp="children"
            >
              {modulesList.map((m) => (
                <Option key={m.modules_id} value={m.modules_id}>
                  {m.modules_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
            <Button
              onClick={() => {
                setIsCopyModalOpen(false);
                copyForm.resetFields();
                setCopyTargetCourse('');
                setCopyTargetModule('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={uploadState.copying}
              disabled={!copyTargetCourse || !copyTargetModule}
            >
              Copy
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Bulk Delete Modal */}
      <Modal
        title="Delete Selected Videos"
        open={isBulkDeleteModalOpen}
        onCancel={() => setIsBulkDeleteModalOpen(false)}
        footer={null}
        destroyOnClose
        centered
      >
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <ExclamationCircleOutlined style={{ fontSize: 44, color: '#ff4d4f', marginBottom: 16 }} />
          <h3 style={{ color: 'var(--text-color)' }}>Are you sure?</h3>
          <p style={{ color: 'var(--text-secondary)' }}>
            You are about to delete <strong style={{ color: '#ff4d4f' }}>{selectedVideos.size}</strong> video(s).
          </p>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            This action will permanently delete these videos from this module.
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
          <Button onClick={() => setIsBulkDeleteModalOpen(false)}>Cancel</Button>
          <Button type="primary" danger onClick={handleBulkDeleteSubmit} loading={uploadState.deleting}>
            Delete {selectedVideos.size} Videos
          </Button>
        </div>
      </Modal>

      {/* Edit Video Modal */}
      <Modal
        title="Edit Video Metadata"
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingItem(null);
          editForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        centered
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditVideoSubmit}>
          <Form.Item
            name="video_serial_number"
            label="Sl No."
            rules={[{ required: true, message: 'Please enter Sl No.' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="video_title"
            label="Video Title"
            rules={[{ required: true, message: 'Please enter video title' }]}
          >
            <Input placeholder="Enter video title" />
          </Form.Item>

          <Form.Item
            name="video_description"
            label="Video Description"
          >
            <Input.TextArea placeholder="Enter video description" rows={3} />
          </Form.Item>

          <Form.Item
            name="instructor_id"
            label="Instructor"
            rules={[{ required: true, message: 'Please select an instructor' }]}
          >
            <Select
              placeholder="Select instructor"
              showSearch
              optionFilterProp="children"
            >
              {instructorsList
                .filter((inst) => inst.status === 'Active' || inst.instructor_id === editingItem?.instructor_id)
                .map((inst) => (
                  <Option key={inst.instructor_id} value={inst.instructor_id}>
                    {inst.instructor_name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
            <Button onClick={() => {
              setIsEditModalOpen(false);
              setEditingItem(null);
              editForm.resetFields();
            }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default VideoIndexPage;
